import { Delegate } from './delegate.js';

class MegaMenuLevels extends HTMLElement {
  #delegate = null;
  #scrollObserver = null;
  #openObserver = null;
  #details = null;
  #megaMenu = null;
  #header = null;
  #onMenuShown = null;
  #onResize = null;

  connectedCallback() {
    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="open-mega-panel"]', this.#onTriggerClick);
    this.#delegate.on('scroll', '[data-mega-scroll]', this.#onScroll, { passive: true, capture: true });

    const scrollAreas = this.querySelectorAll('[data-mega-scroll-area]');
    this.#scrollObserver = new ResizeObserver(() => this.updateScrollStates());
    scrollAreas.forEach((area) => {
      const scrollEl = area.querySelector('[data-mega-scroll]');
      if (scrollEl) this.#scrollObserver.observe(scrollEl);

      const content = area.querySelector('.mega-menu__list--secondary, .mega-menu__panels');
      if (content) this.#scrollObserver.observe(content);
    });

    this.#details = this.closest('details[is="details-mega"]');
    this.#megaMenu = this.closest('.mega-menu');
    this.#header = this.closest('header');
    this.#onMenuShown = () => {
      this.syncInlineStart();
      this.updateScrollStates();
    };
    this.#details?.addEventListener('menu:handleAfterShow', this.#onMenuShown);

    this.#onResize = () => this.syncInlineStart();
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#openObserver = new MutationObserver(() => {
      if (this.#details?.hasAttribute('open') || this.#megaMenu?.hasAttribute('open')) {
        this.syncInlineStart();
        this.scheduleScrollStateUpdate();
      }
    });
    if (this.#details) {
      this.#openObserver.observe(this.#details, { attributes: true, attributeFilter: ['open'] });
    }
    if (this.#megaMenu) {
      this.#openObserver.observe(this.#megaMenu, { attributes: true, attributeFilter: ['open'] });
    }

    this.syncInlineStart();
    this.scheduleScrollStateUpdate();
  }

  disconnectedCallback() {
    this.#delegate?.destroy();
    this.#delegate = null;
    this.#details?.removeEventListener('menu:handleAfterShow', this.#onMenuShown);
    this.#onMenuShown = null;
    window.removeEventListener('resize', this.#onResize);
    this.#onResize = null;
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = null;
    this.#openObserver?.disconnect();
    this.#openObserver = null;
    this.#details = null;
    this.#megaMenu = null;
    this.#header = null;
  }

  /** Match the centered top nav track so columns start under the first menu item. */
  syncInlineStart() {
    const menu = this.#header?.querySelector('.header__navigation .header__menu');
    if (!menu || !this.#header) return;

    const width = Math.round(menu.getBoundingClientRect().width);
    if (width <= 0) return;

    const firstLink = menu.querySelector('ul > li .menu__item, ul > li summary');
    const itemPad = firstLink
      ? Math.round(parseFloat(getComputedStyle(firstLink).paddingInlineStart) || 0)
      : 16;

    this.#header.style.setProperty('--header-menu-width', `${width}px`);
    this.#header.style.setProperty('--mega-menu-item-pad', `${itemPad}px`);
  }

  scheduleScrollStateUpdate() {
    requestAnimationFrame(() => this.updateScrollStates());
  }

  #onTriggerClick = (event, trigger) => {
    const targetId = trigger.getAttribute('data-toggle-id');
    if (!targetId) return;

    const item = trigger.closest('.mega-menu__item');
    // Already open → allow the link to navigate
    if (item?.classList.contains('is-active') || trigger.getAttribute('aria-expanded') === 'true') {
      const activePanel = this.querySelector(`#${CSS.escape(targetId)}`);
      if (activePanel && !activePanel.hasAttribute('hidden')) return;
    }

    event.preventDefault();
    this.activate(trigger);
  };

  #onScroll = (event, scrollEl) => {
    const area = scrollEl.closest('[data-mega-scroll-area]');
    if (!area) return;

    const atEnd = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1;
    area.classList.toggle('is-scrolled-end', atEnd);
  };

  updateScrollStates() {
    this.querySelectorAll('[data-mega-scroll-area]').forEach((area) => {
      const scrollEl = area.querySelector('[data-mega-scroll]');
      if (!scrollEl) return;

      const isScrollable = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
      area.classList.toggle('is-scrollable', isScrollable);

      const atEnd = !isScrollable || scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 1;
      area.classList.toggle('is-scrolled-end', atEnd);
    });
  }

  activate(trigger) {
    const targetId = trigger.getAttribute('data-toggle-id');

    this.querySelectorAll('[data-action="open-mega-panel"]').forEach((el) => {
      const isActive = el === trigger;
      el.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      el.closest('.mega-menu__item')?.classList.toggle('is-active', isActive);
    });

    this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      const isActive = Boolean(targetId) && panel.id === targetId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const panelsScroll = this.querySelector('.mega-menu__scroll-area--panels [data-mega-scroll]');
    if (panelsScroll) panelsScroll.scrollTop = 0;

    requestAnimationFrame(() => this.scheduleScrollStateUpdate());
  }
}

if (!customElements.get('mega-menu-levels')) {
  customElements.define('mega-menu-levels', MegaMenuLevels);
}
